import { Card, CardContent } from '@/components/ui/card';
import { Mail, Phone, MapPin } from 'lucide-react';
import type { PublicBusinessContactInfo } from '@/lib/api';

interface DocumentNotFoundProps {
  documentType: 'Invoice' | 'Quote' | 'Purchase Order' | 'Credit Memo';
  business?: PublicBusinessContactInfo | null;
}

export function DocumentNotFound({ documentType, business }: DocumentNotFoundProps) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center space-y-6">
          {/* Logo if available */}
          {business?.logo && (
            <div className="flex justify-center">
              <img
                src={business.logo}
                alt={business.name}
                className="h-16 w-auto object-contain"
              />
            </div>
          )}

          {/* Message */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">
              Could not find {documentType}
            </h1>
            <p className="text-gray-600">
              This document may have been removed or is no longer available.
            </p>
          </div>

          {/* Business contact info */}
          {business && (
            <div className="border-t pt-6">
              <p className="text-sm text-gray-500 mb-4">
                Get in touch with {business.name}
              </p>
              <div className="space-y-3 text-left">
                {business.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <a
                      href={`mailto:${business.email}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {business.email}
                    </a>
                  </div>
                )}
                {business.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <a
                      href={`tel:${business.phone}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {business.phone}
                    </a>
                  </div>
                )}
                {business.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <span className="text-sm text-gray-600 whitespace-pre-line">
                      {business.address}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fallback if no business info */}
          {!business && (
            <p className="text-sm text-gray-500">
              Please contact the sender of this document for more information.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
